import { type RefObject, useLayoutEffect, useState } from 'react';

const SCROLL_MARGIN = 32;

/**
 * Fallback for the card's measured width, which must stay in sync with the `card`
 * style in `SuggestionCard.tsx`.
 *
 * `cardWidth` is read off the ref during render, so it is 0 on the first render after
 * the card mounts (the ref is only attached on commit) — and the horizontal observer
 * built from that render would use a right inset of just `SCROLL_MARGIN`, reporting
 * room on the right for any target with ≥32px to spare.
 */
const CARD_WIDTH_FALLBACK = 280;

type UseSuggestionCardPositioningProps = {
	cardRef: RefObject<HTMLDivElement>;
	id?: string;
	root?: Element;
	targetBase?: HTMLSpanElement;
	targetEnd?: HTMLSpanElement;
	targetStart?: HTMLSpanElement;
};

type UseSuggestionCardPositioningResult = {
	isCardBottomBelowViewport: boolean;
	isMeasured: boolean;
	isTargetAboveViewport: boolean;
	isTargetBelowViewport: boolean;
	isTargetEndAboveViewport: boolean;
	isTargetEndBelowViewport: boolean;
	isTargetRightOfViewport: boolean;
};

/**
 * Tracks the position of the suggestion card target relative to the editor viewport
 * using IntersectionObserver. Returns booleans describing where the target/card sit
 * relative to the viewport; the consumer positions the card off the CSS anchors.
 */
export const useSuggestionCardPositioning = ({
	id,
	root,
	targetStart,
	targetBase,
	targetEnd,
	cardRef,
}: UseSuggestionCardPositioningProps): UseSuggestionCardPositioningResult => {
	const [isTargetAboveViewport, setIsTargetAboveViewport] = useState(false);
	const [isTargetBelowViewport, setIsTargetBelowViewport] = useState(false);
	const [isCardBottomBelowViewport, setIsCardBottomBelowViewport] = useState(false);
	const [isTargetRightOfViewport, setIsTargetRightOfViewport] = useState(false);
	const [isTargetEndAboveViewport, setIsTargetEndAboveViewport] = useState(false);
	const [isTargetEndBelowViewport, setIsTargetEndBelowViewport] = useState(false);
	const [isMeasured, setIsMeasured] = useState(false);

	const cardHeight = cardRef.current?.offsetHeight ?? 0;
	// `|| CARD_WIDTH_FALLBACK` rather than `??` so a 0 from an unmounted (or
	// not-yet-laid-out) card also falls back instead of collapsing the inset.
	const cardWidth = cardRef.current?.offsetWidth || CARD_WIDTH_FALLBACK;

	/**
	 * Seed the flags for a new target synchronously, before paint. The observers below
	 * deliver asynchronously, so relying on them for the initial value left the first
	 * painted frame of a navigation describing the *previous* target — the card would
	 * paint once in the old mode, then correct itself.
	 *
	 * Keyed on the target, not on the card size, so an unrelated re-render can't re-run
	 * it. The comparisons mirror each observer's `rootMargin`; from here on the
	 * observers own their own flags.
	 */
	useLayoutEffect(() => {
		const card = cardRef.current;
		if (!id || !root || !targetStart || !targetEnd || !targetBase || !card) {
			return;
		}

		const rootRect = root.getBoundingClientRect();
		// IntersectionObserver resolves an element root against its content-clip box —
		// the padding box minus scrollbars — which is what client* describes.
		const rootTop = rootRect.top + root.clientTop;
		const rootBottom = rootTop + root.clientHeight;
		const rootRight = rootRect.left + root.clientLeft + root.clientWidth;

		const height = card.offsetHeight;
		const width = card.offsetWidth || CARD_WIDTH_FALLBACK;

		const startRect = targetStart.getBoundingClientRect();
		const endRect = targetEnd.getBoundingClientRect();
		const baseRect = targetBase.getBoundingClientRect();

		setIsTargetAboveViewport(startRect.top < rootTop + SCROLL_MARGIN);
		setIsCardBottomBelowViewport(startRect.bottom > rootBottom - (height + SCROLL_MARGIN));
		setIsTargetBelowViewport(startRect.top > rootBottom - SCROLL_MARGIN);
		setIsTargetEndAboveViewport(endRect.top < rootTop);
		setIsTargetEndBelowViewport(endRect.bottom > rootBottom - (height + SCROLL_MARGIN + 24));
		setIsTargetRightOfViewport(baseRect.right > rootRight - (width + SCROLL_MARGIN));
		setIsMeasured(true);
	}, [id, root, targetStart, targetBase, targetEnd, cardRef]);

	useLayoutEffect(() => {
		if (!id || !root || !targetStart || !targetEnd || !cardRef.current || !targetBase) {
			setIsTargetRightOfViewport(false);
			setIsTargetEndAboveViewport(false);
			setIsTargetEndBelowViewport(false);
			setIsTargetBelowViewport(false);
			setIsTargetAboveViewport(false);
			setIsCardBottomBelowViewport(false);
			setIsMeasured(false);
			return;
		}

		const verticalCardPositionObserver = new IntersectionObserver(
			(entries) => {
				const [entry] = entries;
				if (!entry || !entry.rootBounds) {
					return;
				}
				const { rootBounds, boundingClientRect } = entry;
				setIsTargetAboveViewport(boundingClientRect.top < rootBounds.top);
				setIsCardBottomBelowViewport(boundingClientRect.bottom > rootBounds.bottom);
			},
			{
				root,
				rootMargin: `-${SCROLL_MARGIN}px 0px -${cardHeight + SCROLL_MARGIN}px 0px`,
			},
		);

		const verticalTargetObserver = new IntersectionObserver(
			(entries) => {
				const [entry] = entries;
				if (!entry || !entry.rootBounds) {
					return;
				}
				const { rootBounds, boundingClientRect } = entry;

				setIsTargetBelowViewport(boundingClientRect.top > rootBounds.bottom);
			},
			{
				root,
				rootMargin: `${cardHeight + SCROLL_MARGIN}px 0px -${SCROLL_MARGIN}px 0px`,
			},
		);

		const targetEndObserver = new IntersectionObserver(
			(entries) => {
				const [entry] = entries;
				if (!entry || !entry.rootBounds) {
					return;
				}
				const { rootBounds, boundingClientRect } = entry;
				setIsTargetEndAboveViewport(boundingClientRect.top < rootBounds.top);
				setIsTargetEndBelowViewport(boundingClientRect.bottom > rootBounds.bottom);
			},
			{
				root,
				// Extra 24 for the gap above card and content
				rootMargin: `0px 0px -${cardHeight + SCROLL_MARGIN + 24}px 0px`,
			},
		);

		/**
		 * This observer is for determining if there's enough space on the right side to display the card.
		 * We use arbitrarily large root margins to ensure that the intersection observer detects it even
		 * when the targetBase anchor (anchored to the icon button) is off the screen.
		 *
		 * A hypotheical edge case is if a toolcall's localid position is more than 99999px further than the
		 * doc-level node's local id, but that is really unlikely.
		 */
		const horizontalObserver = new IntersectionObserver(
			(entries) => {
				const [entry] = entries;
				if (!entry || !entry.rootBounds) {
					return;
				}
				const { rootBounds, boundingClientRect } = entry;
				setIsTargetRightOfViewport(boundingClientRect.right > rootBounds.right);
				setIsMeasured(true);
			},
			{
				root,
				rootMargin: `99999px -${cardWidth + SCROLL_MARGIN}px 99999px 0px`,
			},
		);

		verticalCardPositionObserver.observe(targetStart);
		horizontalObserver.observe(targetBase);
		targetEndObserver.observe(targetEnd);
		verticalTargetObserver.observe(targetStart);
		return () => {
			verticalCardPositionObserver.disconnect();
			horizontalObserver.disconnect();
			targetEndObserver.disconnect();
			verticalTargetObserver.disconnect();
		};
	}, [targetStart, targetBase, cardRef, root, targetEnd, cardHeight, cardWidth, id]);

	return {
		isTargetAboveViewport,
		isTargetBelowViewport,
		isCardBottomBelowViewport,
		isTargetRightOfViewport,
		isTargetEndAboveViewport,
		isTargetEndBelowViewport,
		isMeasured,
	};
};
